import PropTypes from 'prop-types';
// material
import { alpha, experimentalStyled as styled } from '@material-ui/core/styles';

// ----------------------------------------------------------------------

const RootStyle = styled('span')(({ theme, styleProps }) => {
  const isLight = theme.palette.mode === 'light';
  const { color, variant } = styleProps;

  // Validate color and provide fallback
  const isValidColor = color && theme.palette[color];
  const safeColor = isValidColor ? color : 'default';

  const styleFilled = (color) => {
    const colorPalette = theme.palette[color];
    if (!colorPalette) {
      // Fallback to default styling if color doesn't exist
      return {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.grey[300]
      };
    }
    return {
      color: colorPalette.contrastText,
      backgroundColor: colorPalette.main
    };
  };

  const styleOutlined = (color) => {
    const colorPalette = theme.palette[color];
    if (!colorPalette) {
      return {
        color: theme.palette.text.primary,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.grey[500]}`
      };
    }
    return {
      color: colorPalette.main,
      backgroundColor: 'transparent',
      border: `1px solid ${colorPalette.main}`
    };
  };

  const styleGhost = (color) => {
    const colorPalette = theme.palette[color];
    if (!colorPalette) {
      return {
        color: theme.palette.text.secondary,
        backgroundColor: alpha(theme.palette.grey[500], 0.16)
      };
    }
    return {
      color: colorPalette[isLight ? 'dark' : 'light'],
      backgroundColor: alpha(colorPalette.main, 0.16)
    };
  };

  return {
    height: 22,
    minWidth: 22,
    lineHeight: 0,
    borderRadius: 8,
    cursor: 'default',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    justifyContent: 'center',
    padding: theme.spacing(0, 1),
    color: theme.palette.grey[800],
    fontSize: theme.typography.pxToRem(12),
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.grey[300],
    fontWeight: theme.typography.fontWeightBold,

    ...(safeColor !== 'default'
      ? {
          ...(variant === 'filled' && { ...styleFilled(safeColor) }),
          ...(variant === 'outlined' && { ...styleOutlined(safeColor) }),
          ...(variant === 'ghost' && { ...styleGhost(safeColor) })
        }
      : {
          ...(variant === 'outlined' && {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.grey[500_32]}`
          }),
          ...(variant === 'ghost' && {
            color: isLight ? theme.palette.text.secondary : theme.palette.common.white,
            backgroundColor: theme.palette.grey[500_16]
          })
        })
  };
});

// ----------------------------------------------------------------------

export default function Label({ color = 'default', variant = 'ghost', children, ...other }) {
  return (
    <RootStyle styleProps={{ color, variant }} {...other}>
      {children}
    </RootStyle>
  );
}

Label.propTypes = {
  children: PropTypes.node,
  color: PropTypes.oneOf(['default', 'primary', 'secondary', 'info', 'success', 'warning', 'error', 'purple']),
  variant: PropTypes.oneOf(['filled', 'outlined', 'ghost'])
};
